import {resolveBackground} from '#core/utils/color-utils.js';
import {merge} from '#core/utils/config-utils.js';
import {icon} from '#core/utils/style-utils.js';

import {ApplicationConfig} from '#core/config/application-config.js';
import {registerTileSchema} from '#core/application/schema-registry.js';

import {generateSwitch} from '#core/application/modules/tiles/switch/index.js';
import {generateBetBaseInfo} from '#core/application/modules/tiles/bet-base-info/index.js';
import {BET_FUNDS_INFO_TYPES, generateBetFundsInfo} from '#core/application/modules/tiles/bet-funds-info/index.js';
import {generatePrimaryButton, generateSecondaryButton} from '#core/application/modules/tiles/button/index.js';
import {generateBorder} from '#core/application/modules/tiles/border/index.js';
import {generateErrorNotification} from '#core/application/modules/tiles/notification/index.js';

import BET_RECEIPT from './bet-receipt.palette-schema.yaml';

registerTileSchema(BET_RECEIPT);

export function generateBetReceipt(TILE) {
    const BET_RECEIPT_BET_BASE_INFO = generateBetBaseInfo(TILE.BET_RECEIPT_BET_BASE_INFO);

    const BET_RECEIPT_BETS_TOTAL_INFO_NORMAL = generateBetFundsInfo(TILE.BET_RECEIPT_BETS_TOTAL_INFO);
    const BET_RECEIPT_BETS_TOTAL_INFO_EMPHASIZED = generateBetFundsInfo(
        TILE.BET_RECEIPT_BETS_TOTAL_INFO,
        BET_FUNDS_INFO_TYPES.EMPHASIZED
    );

    return {
        // common on top part
        container: {
            itemSpacing: 0,
            style: {
                // background should be set in factories where this tile is used (thanks to Moj structure, bet receipt doesn't
                // have parent container in Betslip)
                ...resolveBackground(TILE.BET_RECEIPT_CONTAINER_BG),
                padding: 0,
            },
        },
        betReceiptHeader: {
            style: {
                ...resolveBackground(TILE.BET_RECEIPT_SUCCESSFUL_BET_PLACEMENT_NOTIFICATION.SUCCESS_BG),
                ...generateBorder(TILE.BET_RECEIPT_SUCCESSFUL_BET_PLACEMENT_NOTIFICATION.SUCCESS_BORDER),
                minHeight: '40px',
                padding: '4px 8px',
                margin: '8px 8px 0',
            },
        },
        headerIcon: {
            src: 'leg-status-won-icon',
            style: {
                minWidth: '24px',
                minHeight: '24px',
                padding: '0px 8px 0px 0px',
                fill: TILE.BET_RECEIPT_SUCCESSFUL_BET_PLACEMENT_NOTIFICATION.SUCCESS_ICON_COLOR,
            },
        },
        betReceiptHeaderSuccessText: {
            style: {
                ...TILE.BET_RECEIPT_SUCCESSFUL_BET_PLACEMENT_TITLE_TEXT,
            },
        },
        betReceiptHeaderDateText: {
            style: {
                ...TILE.BET_RECEIPT_SUCCESSFUL_BET_PLACEMENT_DATE_TEXT,
            },
        },
        shareBet: {
            container: {
                style: {
                    flexGrow: 0,
                },
            },
            shareButton: {
                button: {
                    style: {
                        base: {
                            ...TILE.BET_RECEIPT_SHARE_BET_BUTTON.TERTIARY_BUTTON_TEXT,
                        },
                    },
                },
                buttonIcon: {
                    style: {
                        fill: TILE.BET_RECEIPT_SHARE_BET_BUTTON.TERTIARY_BUTTON_ICON_COLOR,
                    },
                },
            },
        },
        hideDownloadPdfButton: ApplicationConfig.hideDownloadPdfButton,
        downloadBetPdfButton: {
            button: {
                style: {
                    base: {
                        ...TILE.BET_RECEIPT_DOWNLOAD_PDF_BUTTON.TERTIARY_BUTTON_TEXT,
                    },
                },
            },
            icon: {
                style: {
                    fill: TILE.BET_RECEIPT_DOWNLOAD_PDF_BUTTON.TERTIARY_BUTTON_ICON_COLOR,
                },
            },
        },
        pdfErrorContainer: merge(
            generateErrorNotification(TILE.BET_RECEIPT_DOWNLOAD_PDF_ERROR_NOTIFICATION).container,
            {
                style: {
                    margin: 8,
                },
            }
        ),
        subscribeEventsToggle: {
            container: {
                style: {
                    padding: '0 16px',
                },
            },
            bellIcon: icon(TILE.BET_RECEIPT_EVENT_NOTIFICATIONS_SWITCH.SWITCH_LABEL_ICON_COLOR),
            toggleButton: generateSwitch(TILE.BET_RECEIPT_EVENT_NOTIFICATIONS_SWITCH, {TRUNCATE_LABEL: true}),
        },
        // bets part
        betReceiptBodyContainer: {
            separator: {
                style: {
                    color: TILE.BET_RECEIPT_BETS_SEPARATOR_COLOR,
                    size: 1,
                },
            },
            style: {
                ...resolveBackground(TILE.BET_RECEIPT_BETS_LIST_CONTAINER_BG),
                padding: '0px 0px 16px',
            },
        },
        betView: {
            container: {
                style: {
                    ...resolveBackground(TILE.BET_RECEIPT_BET_CONTAINER_BG),
                },
            },
            baseOddsContainer: {
                style: {
                    ...resolveBackground(TILE.BET_RECEIPT_BET_CONTAINER_BG),
                },
            },
            headerContainer: {
                style: {
                    ...resolveBackground(TILE.BET_RECEIPT_BET_HEADER_CONTAINER_BG),
                    padding: '8px 16px',
                },
            },
            header: {
                container: {
                    padding: 0,
                },
                style: {
                    lineHeight: 'normal',
                    ...TILE.BET_RECEIPT_BET_HEADER_TEXT,
                },
            },
            legsContainer: {
                style: {
                    padding: '0 24px',
                },
            },
            fundsContainer: {
                style: {
                    padding: '8px 16px',
                },
            },
            totalOdds: merge(BET_RECEIPT_BET_BASE_INFO.betTotalOdds, {
                container: {
                    style: {
                        padding: '0 8px 0 16px',
                    },
                },
            }),
            totalOddsBoost: merge(BET_RECEIPT_BET_BASE_INFO.betTotalOdds, {
                container: {
                    style: {
                        padding: '0 8px 0 16px',
                    },
                },
            }),
            overaskChanged: merge(BET_RECEIPT_BET_BASE_INFO.betTotalOdds, {
                container: {
                    style: {
                        padding: '0 8px 0 16px',
                    },
                },
            }),
            bonusContainer: {
                style: {
                    padding: '0 8px 0 16px',
                },
            },
            oddsBoostIndicator: {
                bonusIndicator: BET_RECEIPT_BET_BASE_INFO.betBonusIndicator,
            },
            betBonusPromotions: {
                bonusIndicator: BET_RECEIPT_BET_BASE_INFO.betBonusIndicator,
            },
            leg: {
                matchAccaHeader: {
                    style: {
                        ...BET_RECEIPT_BET_BASE_INFO.selectionName,
                    },
                },
                eventName: {
                    container: {
                        padding: '4px 8px 4px 0',
                    },
                    style: BET_RECEIPT_BET_BASE_INFO.eventName,
                },
                odds: {
                    style: BET_RECEIPT_BET_BASE_INFO.selectionName,
                },
                multicastSelectionName: {
                    style: BET_RECEIPT_BET_BASE_INFO.selectionName,
                },
                multicastMarketName: {
                    style: BET_RECEIPT_BET_BASE_INFO.marketName,
                },
                selectionInfoContainer: {
                    style: {
                        padding: 0,
                    },
                },
                priceBoostIndicator: BET_RECEIPT_BET_BASE_INFO.priceBoostValue,
                changedOddsIndicator: BET_RECEIPT_BET_BASE_INFO.oddsChangedValue,
                notificationContainer: {
                    style: {
                        padding: 0,
                    },
                },
                oddsBoostIndicator: {
                    bonusIndicator: merge(BET_RECEIPT_BET_BASE_INFO.betBonusIndicator, {
                        container: {
                            style: {
                                padding: '0 0 4px',
                            },
                        },
                    }),
                },
                matchAccaSelectionItem: BET_RECEIPT_BET_BASE_INFO.betBuilderSelectionItem,
                part: {
                    selectionName: {
                        style: BET_RECEIPT_BET_BASE_INFO.selectionName,
                    },
                    marketName: {
                        style: BET_RECEIPT_BET_BASE_INFO.marketName,
                    },
                },
                earlyPayoutIndicator: BET_RECEIPT_BET_BASE_INFO.earlyPayoutIndicator,
                bestOddsIcon: BET_RECEIPT_BET_BASE_INFO.bestOddsIcon,
            },
            stakeInfo: {
                betInfo: BET_RECEIPT_BET_BASE_INFO.betFundsInfo.medium,
            },
            payoutInfo: {
                betInfo: BET_RECEIPT_BET_BASE_INFO.betFundsInfo.medium,
            },
            oddsBoostInfo: {
                betInfo: BET_RECEIPT_BET_BASE_INFO.betFundsInfo.normal,
            },
        },
        receiptNbrAndLinesContainer: {
            style: {
                padding: '0 16px',
            },
        },
        receiptNbr: {
            style: {
                ...TILE.BET_RECEIPT_NUMBER_TEXT,
            },
        },
        deviceId: {
            style: {
                ...TILE.BET_RECEIPT_DEVICE_ID_TEXT,
            },
        },
        typePriceAndLine: {
            style: {
                ...TILE.BET_RECEIPT_TYPE_TEXT,
            },
        },
        // totals part
        totalFundsContainer: {
            itemSpacing: 4,
            style: {
                padding: '16px 8px 8px',
            },
        },
        totalPayable: {
            betInfo: BET_RECEIPT_BETS_TOTAL_INFO_EMPHASIZED,
        },
        primaryTotalStake: {
            betInfo: BET_RECEIPT_BETS_TOTAL_INFO_EMPHASIZED,
        },
        secondaryTotalStake: {
            betInfo: BET_RECEIPT_BETS_TOTAL_INFO_NORMAL,
        },
        freebetStake: {
            betInfo: BET_RECEIPT_BETS_TOTAL_INFO_NORMAL,
        },
        cashStake: {
            betInfo: BET_RECEIPT_BETS_TOTAL_INFO_NORMAL,
        },
        prewager: {
            cash: BET_RECEIPT_BETS_TOTAL_INFO_NORMAL,
            bonus: BET_RECEIPT_BETS_TOTAL_INFO_NORMAL,
        },
        stakeTax: {
            betInfo: BET_RECEIPT_BETS_TOTAL_INFO_NORMAL,
        },
        totalPayoutInfo: {
            payout: {
                betInfo: BET_RECEIPT_BETS_TOTAL_INFO_EMPHASIZED,
            },
            prewager: {
                cash: BET_RECEIPT_BETS_TOTAL_INFO_NORMAL,
                bonus: BET_RECEIPT_BETS_TOTAL_INFO_NORMAL,
            },
            tax: {
                betInfo: BET_RECEIPT_BETS_TOTAL_INFO_NORMAL,
            },
            payoutAfterTax: {
                betInfo: BET_RECEIPT_BETS_TOTAL_INFO_EMPHASIZED,
            },
        },
        oddsBoostInfo: {
            betInfo: BET_RECEIPT_BETS_TOTAL_INFO_NORMAL,
        },
        // buttons part
        buttonsContainer: {
            style: {
                padding: '0 8px',
            },
        },
        clearButton: merge(generatePrimaryButton(TILE.BET_RECEIPT_CLEAR_BUTTON), {
            style: {
                base: {
                    minHeight: '40px',
                    alignItems: 'center',
                },
            },
        }),
        okButton: merge(generatePrimaryButton(TILE.BET_RECEIPT_CONFIRM_BUTTON), {
            style: {
                base: {
                    minHeight: '40px',
                    alignItems: 'center',
                },
            },
        }),
        retainButton: merge(generateSecondaryButton(TILE.BET_RECEIPT_RETAIN_BUTTON), {
            style: {
                base: {
                    minHeight: '40px',
                    alignItems: 'center',
                },
            },
        }),
    };
}
